import { useLocalStorage } from "@rehooks/local-storage";
import { ReactNode, useCallback, useMemo } from "react";

import { ExternalSortManagement } from "components/Filters/types";
import CardCollapsible from "ds/components/Card/Collapsible";
import Counter from "ds/components/Counter";
import { TableGenericStore } from "ds/components/Table/store/useTableStore/types";
import TableSimple from "ds/components/TableSimple";
import { TableCellPerColumn } from "ds/components/TableSimple/types";
import useAnalytics from "hooks/useAnalytics";
import { Space } from "types/generated";

import styles from "./styles.module.css";

type AttachedStack = {
  id: string;
  stackId: string;
  stackName: string;
  isModule: boolean;
  isAutoattached: boolean;
  spaceDetails: Pick<Space, "id" | "name">;
};

type UsedBySectionProps = {
  stacks: Array<AttachedStack>;
  store: TableGenericStore;
  title: string;
  integrationName: string;
  cells: TableCellPerColumn<AttachedStack>;
  filtersStorageKey: string;
  sortConfig: Record<string, string>;
  emptyState: ReactNode;
  externalSortManagement: ExternalSortManagement;
};

const CloudUsedBySection = ({
  stacks,
  store,
  title,
  integrationName,
  cells,
  filtersStorageKey,
  sortConfig,
  emptyState,
  externalSortManagement,
}: UsedBySectionProps) => {
  const id = useMemo(
    () =>
      `${integrationName}-${title.toLowerCase().replaceAll(" ", "-")}-used-by-section-collapsed`,
    [integrationName, title]
  );
  const [isCollapsed, setIsCollapsed] = useLocalStorage<boolean>(id, stacks.length === 0);
  const handleToggle = useCallback(() => {
    setIsCollapsed(!isCollapsed);
  }, [isCollapsed, setIsCollapsed]);

  const trackAnalytics = useAnalytics();

  return (
    <>
      <CardCollapsible
        fullWidth
        className={styles.collapsibleWrapper}
        title={title}
        titleSuffix={<Counter count={stacks.length} size="small" />}
        isCollapsed={isCollapsed}
        onToggle={handleToggle}
        ariaLevel={2}
        ariaLabel={title}
        fullbleed
      >
        <TableSimple
          data={stacks}
          loading={false}
          store={store}
          wrapperClassName={styles.tableWrapper}
          ariaLabel={`${integrationName} used by list: ${title}`}
          trackAnalytics={trackAnalytics}
          cellPerColumn={cells}
          filtersStorageKey={filtersStorageKey}
          sortConfig={sortConfig}
          externalSortManagement={externalSortManagement}
          emptyState={emptyState}
          hasPagination={false}
        />
      </CardCollapsible>
    </>
  );
};

export default CloudUsedBySection;
